class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.loader = this.querySelector('.js-search-loader');
    this.clearBtn = this.querySelector('.js-search-clear');
    this.closeBtn = this.querySelector('.js-search-close');
    this.suggestionsTitles = this.querySelectorAll('.js-search-suggestions-title');
    this.suggestionsSecondary = this.querySelector('.js-search-suggestions-secondary');
    this.queriesContainer = this.querySelector('[data-predictive-search-queries]');
    this.noResultsContainer = this.querySelector('[data-predictive-search-no-results]');
    
    this.openBtns = document.querySelectorAll('.js-search-open');
    this.defaultResults = this.querySelector('#predictive-search-default')?.cloneNode(true);
    this.defaultQueries = this.querySelector('#predictive-search-default-queries')?.cloneNode(true);
    
    this.debounceTimer = null;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Open modal
    this.openBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close modal
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Input changes
    if (this.input) {
      this.input.addEventListener('input', this.onInput.bind(this));
    }

    // Clear input
    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => {
        this.input.value = '';
        this.clearBtn.classList.add('hidden');
        this.input.focus();
        this.showDefault();
      });
    }

    // Close on overlay click
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
  }

  open() {
    this.style.display = 'flex';
    // Small delay to allow display block to apply before transitioning opacity
    setTimeout(() => {
      this.classList.remove('opacity-0', 'pointer-events-none');
      this.dataset.state = 'open';
      if (this.input) this.input.focus();
      document.body.style.overflow = 'hidden';
    }, 10);
  }

  close() {
    this.classList.add('opacity-0', 'pointer-events-none');
    this.dataset.state = 'closed';
    document.body.style.overflow = '';
    
    // Wait for transition
    setTimeout(() => {
      this.style.display = 'none';
    }, 300);
  }

  onInput() {
    const searchTerm = this.input.value.trim();
    
    if (searchTerm.length > 0) {
      this.clearBtn.classList.remove('hidden');
      this.suggestionsTitles.forEach((title) => title.classList.add('opacity-0', 'pointer-events-none'));
      // Right menu column gives its width back to the query results
      if (this.suggestionsSecondary) this.suggestionsSecondary.classList.add('hidden');
    } else {
      this.clearBtn.classList.add('hidden');
      this.showDefault();
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.fetchResults(searchTerm);
    }, 300);
  }

  showDefault() {
    this.classList.remove('has-no-results');
    if (this.defaultResults) {
      this.resultsContainer.innerHTML = '';
      this.resultsContainer.appendChild(this.defaultResults.cloneNode(true));
    } else {
      this.resultsContainer.innerHTML = '';
    }

    if (this.queriesContainer && this.defaultQueries) {
      this.queriesContainer.innerHTML = '';
      this.queriesContainer.appendChild(this.defaultQueries.cloneNode(true));
    }
    
    if (this.noResultsContainer) this.noResultsContainer.innerHTML = '';

    this.suggestionsTitles.forEach((title) => title.classList.remove('opacity-0', 'pointer-events-none'));
    if (this.suggestionsSecondary) this.suggestionsSecondary.classList.remove('hidden');
  }

  fetchResults(searchTerm) {
    if (this.loader) this.loader.classList.remove('hidden');
    if (this.resultsContainer) this.resultsContainer.classList.add('hidden');
    if (this.queriesContainer) this.queriesContainer.classList.add('hidden');
    if (this.noResultsContainer) this.noResultsContainer.innerHTML = '';

    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product,query,collection&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#predictive-search-results');
        const queriesMarkup = html.querySelector('#predictive-search-queries');
        const noResultsMarkup = html.querySelector('#predictive-search-no-results');
        
        if (resultsMarkup && this.resultsContainer) {
          this.resultsContainer.innerHTML = resultsMarkup.innerHTML;
          const resultsContainer = this.resultsContainer;
          window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
          window.GZAnalyticsQueue.push(function (GZAnalytics) {
            GZAnalytics.pushViewItemListFromElements(
              [resultsContainer],
              'search preview',
              { searchTerm: searchTerm }
            );
          });
        }
        
        if (queriesMarkup && this.queriesContainer) {
          this.queriesContainer.innerHTML = queriesMarkup.innerHTML;
        }

        if (this.noResultsContainer) {
          this.noResultsContainer.innerHTML = noResultsMarkup ? noResultsMarkup.innerHTML.trim() : '';
        }

        const hasProducts = resultsMarkup && resultsMarkup.querySelector('.grid') !== null;
        const hasQueries = queriesMarkup && queriesMarkup.querySelector('ul') !== null;
        
        if (!hasProducts && !hasQueries) {
          this.classList.add('has-no-results');
        } else {
          this.classList.remove('has-no-results');
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (this.loader) this.loader.classList.add('hidden');
        if (this.resultsContainer) this.resultsContainer.classList.remove('hidden');
        if (this.queriesContainer) this.queriesContainer.classList.remove('hidden');
        
        // Re-init swipers in results if needed
        if (window.ThemeSwipers && typeof window.ThemeSwipers.initProductCards === 'function') {
          window.ThemeSwipers.initProductCards();
        }
      });
  }
}

customElements.define('gz-predictive-search', PredictiveSearch);
